/**
 * Manages the injection of PrimeVue styles into multiple MFE shadow DOMs.
 * It observes the document.head for new style tags and clones them into
 * every registered MFE, ensuring consistent styling.
 */
export class PrimeVueStyleInjection {
  /**
   * @param {Object} options
   * @param {string[]} [options.ignoreIds=[]]
   * @param {string} [options.styleAttribute="data-primevue-style-id"]
   */
  constructor ({ ignoreIds = [], styleAttribute = 'data-primevue-style-id' } = {}) {
    this.mfeShadowRoots = new Set()
    this.observer = null
    this.ignoreIds = ignoreIds
    this.styleAttribute = styleAttribute
  }

  /**
   * Registers an MFE's root element to receive style injections.
   * @param {HTMLElement} mfeElement
   */
  registerMfe (mfeElement) {
    if (mfeElement && mfeElement.shadowRoot) {
      const shadowRoot = mfeElement.shadowRoot
      if (this.mfeShadowRoots.has(shadowRoot)) return

      this.mfeShadowRoots.add(shadowRoot)
      // Immediately inject any styles that are already in the head.
      this.injectExistingStyles(shadowRoot)
    } else {
      console.warn('[PrimeVueStyleInjection] Attempted to register an element without a shadowRoot.')
    }
  }

  /**
   * Unregisters an MFE's root element when it's unmounted.
   * @param {HTMLElement} mfeElement
   */
  unregisterMfe (mfeElement) {
    if (mfeElement && mfeElement.shadowRoot) {
      this.mfeShadowRoots.delete(mfeElement.shadowRoot)
    }
  }

  /**
   * Injects all currently available styles into a newly registered MFE.
   * @param {ShadowRoot} shadowRoot
   */
  injectExistingStyles (shadowRoot) {
    const existingStyles = document.head.querySelectorAll(`[${this.styleAttribute}]`)
    existingStyles.forEach(styleNode => this.cloneAndInjectStyle(styleNode, shadowRoot))
  }

  /**
   * Clones a style node and injects it into a specific shadow root.
   * @param {HTMLStyleElement | HTMLLinkElement} styleNode
   * @param {ShadowRoot} shadowRoot
   */
  cloneAndInjectStyle (styleNode, shadowRoot) {
    const styleId = styleNode.getAttribute(this.styleAttribute)

    if (styleId && this.ignoreIds.some(ignoreId => styleId.includes(ignoreId))) {
      return
    }

    if (shadowRoot.querySelector(`[${this.styleAttribute}="${styleId}"]`)) {
      return
    }

    const clone = styleNode.cloneNode(true)
    shadowRoot.appendChild(clone)
  }

  start () {
    console.log('[PrimeVueStyleInjection] Starting style injection observer...')
    if (this.observer) return

    const targetNode = document.head
    const config = { childList: true }

    const callback = (mutationList) => {
      for (const mutation of mutationList) {
        mutation.addedNodes.forEach(node => {
          if (
            (node.tagName === 'STYLE' || node.tagName === 'LINK') &&
            node.hasAttribute(this.styleAttribute)
          ) {
            this.mfeShadowRoots.forEach(shadowRoot => {
              this.cloneAndInjectStyle(node, shadowRoot)
            })
          }
        })
      }
    }

    this.observer = new MutationObserver(callback)
    this.observer.observe(targetNode, config)
  }

  stop () {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }
}

export const primeVueStyleInjection = new PrimeVueStyleInjection({
  ignoreIds: ['variables', 'tooltip'],
})
